'use client';
import React from 'react';
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { enUS, esES, frFR, deDE, zhCN } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en-au';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/zh-cn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTranslation } from '@/contexts/TranslationContext';

const muiText = (bundle) => bundle.components.MuiLocalizationProvider.defaultProps.localeText;

const LOCALE_TO_DAYJS = {
  'en-US':   { lang: 'en',    format: 'MM/DD/YYYY', localeText: muiText(enUS) },
  'en-GB':   { lang: 'en-gb', format: 'DD/MM/YYYY', localeText: muiText(enUS) },
  'en-AU':   { lang: 'en-au', format: 'DD/MM/YYYY', localeText: muiText(enUS) },
  'en-CA':   { lang: 'en',    format: 'MM/DD/YYYY', localeText: muiText(enUS) },
  'es-ES':   { lang: 'es',    format: 'DD/MM/YYYY', localeText: muiText(esES) },
  'fr-FR':   { lang: 'fr',    format: 'DD/MM/YYYY', localeText: muiText(frFR) },
  'de-DE':   { lang: 'de',    format: 'DD/MM/YYYY', localeText: muiText(deDE) },
  'zh-Hans': { lang: 'zh-cn', format: 'YYYY/MM/DD', localeText: muiText(zhCN) },
};

const StyledDatePicker = ({
  value,
  onChange,
  name,
  id,
  disabled = false,
  placeholder = '',
  className = '',
  sx = {},
  inputProps = {},
  InputProps = {},
  PopperProps = {},
  slotProps = {},
  slots = {},
  iconTop = '95%',
  minHeight = '45px',
  padding = '15px',
  iconColor = 'white',
  textColor = '#1e2a35',
  ...other
}) => {
  const { locale } = useTranslation();
  const { lang, format, localeText } = LOCALE_TO_DAYJS[locale] || LOCALE_TO_DAYJS['en-US'];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={lang} localeText={localeText}>
      <DatePicker
        value={value}
        onChange={onChange}
        disabled={disabled}
        name={name}
        id={id}
        format={format}
        className={className}
        sx={{
          // Target the MuiFormControl root that wraps the TextField
          '& .MuiFormControl-root': {
            backgroundColor: 'transparent !important',
          },
          // Ensure inner input and base are also transparent
          '& .MuiInputBase-root': {
            backgroundColor: 'transparent !important',
            '& .MuiInputBase-input': {
              backgroundColor: 'transparent !important',
            },
          },
        }}
        slotProps={{
          textField: {
            variant: 'standard',
            fullWidth: true,
            InputProps: {
              disableUnderline: true,
            },
            sx: {
              '& .MuiInputBase-root': {
                border: 'none',
                backgroundColor: 'transparent',
                borderRadius: '4px',
                minHeight: minHeight,
                padding: `0 40px 0 ${padding}`,
                fontSize: '17px',
                fontWeight: 500,
                color: `${iconColor}`,

                '&.Mui-focused': {
                  borderColor: 'red',
                },
              },
            },
          },
          // Correct the alignment of the calendar icon
          openPickerButton: {
            sx: {
              position: 'absolute',
              right: '10px',
              top: iconTop,
              transform: 'translateY(-50%)',
              padding: '0',
            },
          },
        }}
        slots={{
          openPickerIcon: () => <CalendarTodayIcon sx={{ color: iconColor }} />,
        }}
        {...other}
      />
    </LocalizationProvider>
  );
};

export default StyledDatePicker;
